import { create, type StateCreator } from "zustand";
import { persist, devtools } from "zustand/middleware";
import { createAuthGuardedStorage, createAuthSlice, type AuthSlice } from "@njbsoft/store";
import { createThemeSlice, type ThemeSlice } from "@njbsoft/store";
import { createLocaleSlice, type LocaleSlice } from "@njbsoft/store";
import { type PersonaSnapshot } from "@/lib/resolve-persona";

// The portal's only navigation context: the id of the company the caller works
// for. A person holds at most one live membership
// (`idx_company_members_live_profile`), so this is a cached lookup rather than
// a choice — nothing in the portal switches it, and `resolveActiveMembership`
// heals it when the membership it names has ended. Companies have NO org
// context by construction (company_user claims are org-less), so there is no
// org slice here.
export interface CompanyContextSlice {
  activeCompanyId: string | null;
  setActiveCompanyId: (id: string | null) => void;
}

const createCompanyContextSlice: StateCreator<CompanyStore, [], [], CompanyContextSlice> = (
  set,
) => ({
  activeCompanyId: null,
  setActiveCompanyId: (id) => set({ activeCompanyId: id }),
});

// What survives a reload so an offline mount still knows who the caller is.
// The snapshot answers "which portal personas did the server last confirm";
// the identity answers "whose queued work is this" while /field/me is
// unreachable.
export interface PersonaSlice {
  personaSnapshot: PersonaSnapshot | null;
  setPersonaSnapshot: (snapshot: PersonaSnapshot | null) => void;
  offlineIdentity: { profileId: string; displayName: string } | null;
  setOfflineIdentity: (identity: { profileId: string; displayName: string } | null) => void;
}

function samePersonaSnapshot(
  a: PersonaSlice["personaSnapshot"],
  b: PersonaSlice["personaSnapshot"],
): boolean {
  if (a === null || b === null) return a === b;
  if (a.membership?.companyId !== b.membership?.companyId) return false;
  if (a.membership?.role !== b.membership?.role) return false;
  // Element-wise, in order: the caller maps this list off the probe's rows on
  // every render, so the reference always differs and only the VALUES can say
  // whether anything moved.
  if (a.grants.length !== b.grants.length) return false;
  return a.grants.every((grant, i) => {
    const other = b.grants[i];
    return (
      other !== undefined &&
      grant.orgId === other.orgId &&
      grant.orgName === other.orgName &&
      grant.status === other.status
    );
  });
}

/**
 * The persisted snapshot, or null for anything that is not one.
 *
 * A payload written by a build that persisted the OLD shape
 * (`{member, tester}`) reads as ABSENT: pre-production, there is no migration
 * shim, and the next completed probe pair rewrites it. Absent is also the safe
 * direction — it resolves an offline mount to the offline screen rather than
 * admitting one on a shape nobody can interpret.
 */
// The cast is the whole point of the guard: a parsed JSON value is `unknown`
// until something proves it is a keyed object, and this is that proof.
function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readPersistedMembership(value: unknown): PersonaSnapshot["membership"] | false {
  if (value === null || value === undefined) return null;
  const row = asRecord(value);
  if (row === null) return false;
  if (typeof row.companyId !== "string") return false;
  if (row.role !== "admin" && row.role !== "tester") return false;
  return { companyId: row.companyId, role: row.role };
}

function readPersistedPersonaSnapshot(value: unknown): PersonaSnapshot | null {
  const candidate = asRecord(value);
  if (candidate === null) return null;
  if (!Array.isArray(candidate.grants)) return null;

  const membership = readPersistedMembership(candidate.membership);
  if (membership === false) return null;

  const grants: PersonaSnapshot["grants"] = [];
  for (const entry of candidate.grants) {
    const row = asRecord(entry);
    if (row === null) return null;
    // The status is read as a plain string, then widened by the cast below:
    // only `active` and `suspended` grant standing, so a value this build has
    // never seen is inert rather than a reason to discard the whole snapshot.
    if (
      typeof row.orgId !== "string" ||
      typeof row.orgName !== "string" ||
      typeof row.status !== "string"
    ) {
      return null;
    }
    grants.push({
      orgId: row.orgId,
      orgName: row.orgName,
      status: row.status as PersonaSnapshot["grants"][number]["status"],
    });
  }

  return { membership, grants };
}

function sameOfflineIdentity(
  a: PersonaSlice["offlineIdentity"],
  b: PersonaSlice["offlineIdentity"],
): boolean {
  if (a === null || b === null) return a === b;
  return a.profileId === b.profileId && a.displayName === b.displayName;
}

// Both setters compare by VALUE and return without calling `set()` when nothing
// changed. That is structural, not an optimization: their caller writes from an
// effect on every settled probe pair, and an unconditional `set()` would store a
// new object reference each time, wake every selector of it, re-render, and
// re-run the effect. The dependency array on that effect is a convention; this
// is the guard that holds when a future caller keys one wrongly.
const createPersonaSlice: StateCreator<CompanyStore, [], [], PersonaSlice> = (set, get) => ({
  personaSnapshot: null,
  setPersonaSnapshot: (snapshot) => {
    if (samePersonaSnapshot(get().personaSnapshot, snapshot)) return;
    set({ personaSnapshot: snapshot });
  },
  offlineIdentity: null,
  setOfflineIdentity: (identity) => {
    if (sameOfflineIdentity(get().offlineIdentity, identity)) return;
    set({ offlineIdentity: identity });
  },
});

// Whether the caller waved the install card away. Persisted, because a prompt
// that returns on every reload is the nag the tiering exists to avoid; it says
// nothing about what is stored, only about what is offered.
export interface InstallPromptSlice {
  installPromptDismissed: boolean;
  setInstallPromptDismissed: (dismissed: boolean) => void;
}

const createInstallPromptSlice: StateCreator<CompanyStore, [], [], InstallPromptSlice> = (set) => ({
  installPromptDismissed: false,
  setInstallPromptDismissed: (dismissed) => set({ installPromptDismissed: dismissed }),
});

type CompanyStore = AuthSlice &
  ThemeSlice &
  LocaleSlice &
  CompanyContextSlice &
  PersonaSlice &
  InstallPromptSlice;

/** The localStorage key the persist middleware writes — the channel sibling
 *  tabs observe each other's token rotations and sign-outs through. */
export const COMPANY_STORE_PERSIST_KEY = "njbsoft-company-store";

/** What survives a reload. Lifted out of the persist options so the
 *  guarded storage can be typed on the PERSISTED shape rather than the whole
 *  store — the two must agree, and deriving one from the other is what keeps
 *  them agreeing. */
const partializeSession = (state: CompanyStore) => ({
  // Access token persisted alongside refresh token -- see security.md for threat model.
  token: state.token,
  refreshToken: state.refreshToken,
  expiresAt: state.expiresAt,
  // Ordered against what is already persisted before every write.
  rotatedAt: state.rotatedAt,
  user: state.user,
  themePreference: state.themePreference,
  accentColor: state.accentColor,
  localePreference: state.localePreference,
  activeCompanyId: state.activeCompanyId,
  personaSnapshot: state.personaSnapshot,
  offlineIdentity: state.offlineIdentity,
  installPromptDismissed: state.installPromptDismissed,
});
type PersistedCompanyStore = ReturnType<typeof partializeSession>;

/** The session's persist channel, guarded so no write can move the token pair
 *  BACKWARDS (see `@njbsoft/store`'s auth-persist). */
const authStorage = createAuthGuardedStorage<PersistedCompanyStore>();

export const useCompanyStore = create<CompanyStore>()(
  // devtools enabled only in development to avoid leaking state in production
  devtools(
    persist(
      (...a) => ({
        ...createAuthSlice(...a),
        ...createThemeSlice(...a),
        ...createLocaleSlice(...a),
        ...createCompanyContextSlice(...a),
        ...createPersonaSlice(...a),
        ...createInstallPromptSlice(...a),
      }),
      {
        name: COMPANY_STORE_PERSIST_KEY,
        storage: authStorage,
        partialize: partializeSession,
        // `...current` first: a payload written before a key existed hydrates
        // that key at its slice default, so adding one needs no version/migrate.
        // The persona snapshot is the one key that CHANGED shape rather than
        // arriving, so it is read through its validator: an old-shape payload
        // hydrates as absent and the next completed probe pair rewrites it.
        merge: (persisted, current) => ({
          ...current,
          ...(persisted as Partial<CompanyStore>),
          isAuthenticated: !!(persisted as Partial<CompanyStore>)?.refreshToken,
          personaSnapshot: readPersistedPersonaSnapshot(
            (persisted as Partial<CompanyStore>)?.personaSnapshot,
          ),
        }),
      },
    ),
    { name: "SAMS v2 Company", enabled: import.meta.env.DEV },
  ),
);

// The adapter refuses a write that would move the token pair backwards and
// hands back the pair that survived; this catches THIS tab's memory up to it,
// so the context that lost the race is not left holding a spent pair to replay.
authStorage.onKeptNewer((tokens) => {
  useCompanyStore.getState().adoptTokens(tokens);
});
